/**
 * @component ry-browser-mockup
 * @category  display
 * @summary   Pure-visual chrome that frames arbitrary content to look like a desktop browser window. Top bar shows the macOS traffic-light dots; an optional URL bar shows a fake address. The content area is a slot for anything.
 *
 * Ported from daisyUI's `mockup-browser` (MIT). See
 * demos/parity/vendor/daisy/mockup-browser/component.css for the upstream
 * source kept verbatim for parity comparison.
 *
 * Function/Form/Theme split:
 * - FUNCTION (this file): if [url] is present, generates a chrome row with
 *   three traffic-light dots + a URL bar. If absent, only the dots row is
 *   rendered. The content area is always present and wraps whatever children
 *   the consumer authored.
 * - FORM (`ry-browser-mockup.structure.css`): block layout, dot row, URL bar
 *   sizing, content area.
 * - THEME (`ry-browser-mockup.theme.css`): traffic-light dot colors, URL bar
 *   bg, outer border + radius + shadow.
 *
 * @attribute url      string?                       Text displayed inside the fake URL bar. If absent, the URL bar is NOT rendered — only the traffic-light dot row appears above the content.
 * @attribute variant  'default'|'border'  default='default'  `border` adds a visible outer border + divider between the chrome row and content for a flatter, framed look. `default` uses the standard shadowed chrome.
 *
 * @content   Any HTML. Lives inside the content area and inherits no styling
 *           beyond the chrome's outer radius/overflow clipping.
 *
 * @dom
 *   ry-browser-mockup[url?][variant?]
 *     > [data-ry-target="chrome"].ry-browser-mockup__chrome
 *         > .ry-browser-mockup__dots
 *             > .ry-browser-mockup__dot.ry-browser-mockup__dot--red
 *             > .ry-browser-mockup__dot.ry-browser-mockup__dot--yellow
 *             > .ry-browser-mockup__dot.ry-browser-mockup__dot--green
 *         > [data-ry-target="url"].ry-browser-mockup__url   (only when [url])
 *     > [data-ry-target="content"].ry-browser-mockup__content
 *         > {slotted children}
 *
 * @gotcha  This is purely visual. There is no event API, no interaction, and
 *          no focus handling. The element renders nothing on its own beyond
 *          decorative chrome — every interactive thing must come from your
 *          children.
 *
 * @gotcha  Changing [url] after first render currently does NOT re-render the
 *          chrome row. Set [url] before mount, or remove and re-add the
 *          element to swap visibility. (Live attribute changes can be added
 *          later via observedAttributes if a real need surfaces.)
 *
 * @example basic-with-url
 * ```html
 * <ry-browser-mockup url="https://ry-ui.dev">
 *   <div style="padding: 2rem; text-align: center;">Page content goes here.</div>
 * </ry-browser-mockup>
 * ```
 *
 * @example no-url
 * ```html
 * <ry-browser-mockup>
 *   <div style="padding: 2rem; text-align: center;">No URL bar — just chrome dots.</div>
 * </ry-browser-mockup>
 * ```
 *
 * @example border-variant
 * ```html
 * <ry-browser-mockup url="https://example.com" variant="border">
 *   <div style="padding: 2rem; text-align: center;">Framed variant.</div>
 * </ry-browser-mockup>
 * ```
 *
 * @tutorial renders-chrome-and-content
 * ```js
 * // Chrome row is always present; content slot wraps original children.
 * const el = mount('<ry-browser-mockup><p id="p">hi</p></ry-browser-mockup>');
 * await expectEqual(!!el.querySelector('[data-ry-target="chrome"]'), true, 'chrome row rendered');
 * await expectEqual(!!el.querySelector('[data-ry-target="content"] #p'), true, 'content slot wraps children');
 * ```
 *
 * @tutorial url-attribute-renders-bar
 * ```js
 * const el = mount('<ry-browser-mockup url="https://example.com"></ry-browser-mockup>');
 * const bar = el.querySelector('[data-ry-target="url"]');
 * await expectEqual(!!bar, true, 'url bar rendered when [url] present');
 * await expectEqual(bar.textContent.trim(), 'https://example.com', 'url bar shows the attribute text');
 * ```
 *
 * @tutorial no-url-no-bar
 * ```js
 * // Wrong assumption: an empty/missing url renders an empty bar. It does NOT
 * // — the bar element is omitted entirely so layout shrinks to just the dots.
 * const el = mount('<ry-browser-mockup></ry-browser-mockup>');
 * await expectEqual(el.querySelector('[data-ry-target="url"]'), null, 'no url bar when [url] absent');
 * ```
 *
 * @see ry-card
 */

import { RyElement } from '../core/ry-element.js';

export class RyBrowserMockup extends RyElement {
  setup(): void {
    // Capture original children to move into the content slot, since we
    // restructure the DOM.
    const original = Array.from(this.childNodes);

    const url = this.getAttribute('url');

    const chrome = document.createElement('div');
    chrome.setAttribute('data-ry-target', 'chrome');
    chrome.className = 'ry-browser-mockup__chrome';

    const dots = document.createElement('div');
    dots.className = 'ry-browser-mockup__dots';
    for (const color of ['red', 'yellow', 'green'] as const) {
      const dot = document.createElement('span');
      dot.className = `ry-browser-mockup__dot ry-browser-mockup__dot--${color}`;
      dots.appendChild(dot);
    }
    chrome.appendChild(dots);

    if (url !== null) {
      const bar = document.createElement('div');
      bar.setAttribute('data-ry-target', 'url');
      bar.className = 'ry-browser-mockup__url';
      bar.textContent = url;
      chrome.appendChild(bar);
    }

    const content = document.createElement('div');
    content.setAttribute('data-ry-target', 'content');
    content.className = 'ry-browser-mockup__content';
    for (const node of original) content.appendChild(node);

    this.replaceChildren(chrome, content);
  }
}

customElements.define('ry-browser-mockup', RyBrowserMockup);
